'use client';

import { useState } from 'react';
import { ConfirmDialog } from './ConfirmDialog';

export interface ProjectSummary {
  id: string;
  title: string;
  status: string;
  aspectRatio: string;
  updatedAt: string;
  thumbnailUrl?: string;
}

interface ProjectHubProps {
  projects: ProjectSummary[];
  onOpen: (id: string) => void;
  onCreate: () => void;
  onDelete: (id: string) => Promise<void> | void;
  busy: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Черновик',
  rendering: 'Рендерится',
  rendered: 'Готов',
  failed: 'Ошибка',
};

const STATUS_STYLE: Record<string, string> = {
  draft: 'bg-white/10 text-white/70',
  rendering: 'bg-amber-400/15 text-amber-300',
  rendered: 'bg-emerald-400/15 text-emerald-300',
  failed: 'bg-red-400/15 text-red-300',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Thumbnail({ url }: { url?: string }) {
  if (url) {
    return (
      <video
        src={`${url}#t=0.5`}
        muted
        playsInline
        preload="metadata"
        className="absolute inset-0 h-full w-full object-cover"
      />
    );
  }
  return (
    <div className="absolute inset-0 flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-600/25 to-white/5">
      <svg
        className="h-8 w-8 text-white/30"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m10 9 5 3-5 3V9z" />
      </svg>
    </div>
  );
}

export function ProjectHub({ projects, onOpen, onCreate, onDelete, busy }: ProjectHubProps) {
  const [pending, setPending] = useState<ProjectSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!pending) return;
    setDeleting(true);
    try {
      await onDelete(pending.id);
      setPending(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-6xl px-6 pb-16 pt-10">
        <div className="mb-9 flex flex-wrap items-center gap-x-5 gap-y-3">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-medium text-white/35">Студия</span>
            <span className="text-sm text-white/20">/</span>
            <h1 className="text-[30px] font-semibold leading-none text-white">Проекты</h1>
          </div>
          <button
            type="button"
            onClick={onCreate}
            disabled={busy}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-brand-600/30 transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="text-base leading-none">+</span>
            {busy ? 'Создаём…' : 'Новый проект'}
          </button>
        </div>

        <div className="grid grid-cols-1 items-start gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => onOpen(p.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen(p.id);
                }
              }}
              className="group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03] text-left transition hover:border-white/20 hover:bg-white/[0.06] hover:shadow-[0_16px_50px_rgba(91,53,255,0.14)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              {/* preview — uniform landscape thumbnail; real format shown by the badge */}
              <div className="relative aspect-video w-full overflow-hidden bg-black/40">
                <Thumbnail url={p.thumbnailUrl} />
                {/* soft gradient sheen for legibility + depth */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-white/[0.04]" />
                <span
                  className={`absolute left-2.5 top-2.5 rounded-full px-2 py-0.5 text-[11px] font-medium backdrop-blur-md ${
                    STATUS_STYLE[p.status] ?? 'bg-white/10 text-white/70'
                  }`}
                >
                  {STATUS_LABEL[p.status] ?? p.status}
                </span>
                <span className="absolute right-2.5 top-2.5 rounded-md bg-black/50 px-1.5 py-0.5 text-[11px] font-medium text-white/70 backdrop-blur-md">
                  {p.aspectRatio}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPending(p);
                  }}
                  className="absolute bottom-2.5 right-2.5 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white/70 opacity-0 backdrop-blur-md transition hover:bg-red-500/80 hover:text-white focus:opacity-100 focus:outline-none group-hover:opacity-100"
                  aria-label={`Удалить проект «${p.title || 'Без названия'}»`}
                >
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 6h18" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <path d="M10 11v6M14 11v6" />
                  </svg>
                </button>
              </div>

              {/* meta */}
              <div className="flex flex-1 flex-col p-4">
                <div className="line-clamp-1 text-[15px] font-semibold text-white">{p.title || 'Без названия'}</div>
                <div className="mt-2.5 flex items-center justify-between">
                  <span className="text-xs text-white/40">{formatDate(p.updatedAt)}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-300 opacity-80 transition group-hover:translate-x-0.5 group-hover:opacity-100">
                    Открыть →
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ConfirmDialog
        open={!!pending}
        title="Удалить проект?"
        message={`«${pending?.title || 'Без названия'}» будет удалён без возможности восстановления.`}
        confirmLabel="Удалить"
        danger
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!deleting) setPending(null);
        }}
      />
    </div>
  );
}
